/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { Case, Default, Switch } from "react-if";
import { useParams } from "react-router-dom";
import { Provider } from "~/generated/graphql";
import { formatCostObject, getOriginalCost } from "~/components/CostBox";
import { useCurrency, useFeature, useUuidValidator } from "~/hooks";
import { useCustomers } from "~/services/customers";
import { isCloudResource } from "~/tools";
import { useCostSplittingRules } from "../components/hooks";
import { DefaultEstateDetailsPage } from "./DefaultEstateDetailsPage";
import { Ec2InstancePage } from "./Ec2InstancePage";
import { Ec2SecurityGroupPage } from "./Ec2SecurityGroup/Ec2SecurityGroupPage";
import { Ec2SubnetPage } from "./Ec2Subnet/Ec2SubnetPage";
import { Ec2VpcPage } from "./Ec2VPC/Ec2VpcPage";
import { useEstateDetails } from "./hooks";
import { getCloudProviderFlags } from "./ReservationDetails";
import { ReservationInstancePage } from "./ReservationInstancePage";
import { isResourceCostSplitRules } from "./utils";

type RouteParams = {
  nordcloudId: string;
};

export function EstateDetailsPage() {
  const { isAdminLite } = useCustomers();
  const showReservationDetails = useFeature("azureReservationDetails");
  const { nordcloudId = "" } = useParams<RouteParams>();

  useUuidValidator(nordcloudId);

  const { currency } = useCurrency();

  const { data, loading, resourceLifecycleDates, error, refetch } =
    useEstateDetails(nordcloudId);

  const estateRecord = data.estateRecord ?? {
    __typename: "CostResource",
    providerType: "",
    id: "",
  };

  const cloudResourceData = useMemo(
    () =>
      isCloudResource(estateRecord)
        ? {
            name: estateRecord.name,
            providerId: estateRecord.providerId,
            provider: estateRecord.provider,
            providerType: estateRecord.providerType,
          }
        : null,
    [estateRecord]
  );
  const { isReservationProvider } = getCloudProviderFlags(estateRecord);
  const showReservationInstancePage =
    isReservationProvider && showReservationDetails;

  const {
    costSplitRules,
    refetch: refetchRules,
    loading: isLoadingRules,
  } = useCostSplittingRules(estateRecord.id);

  const showApplicationsList =
    estateRecord.id &&
    isResourceCostSplitRules(
      loading,
      costSplitRules?.resourceCostSplitRules.count ?? undefined
    );

  const costs = useMemo(
    () =>
      formatCostObject({
        data: {
          currentMonth: data.estateRecord?.cost?.currentMonth,
          forecast: data.estateRecord?.cost?.forecast,
          previousMonth: data.estateRecord?.cost?.previousMonth,
          originalCost: getOriginalCost(data.estateRecord?.cost),
          co2e: data.estateRecord?.co2e?.currentMonth,
          co2eSavings: isCloudResource(estateRecord)
            ? estateRecord.savingsAmount?.co2e
            : "0",
          cloudWasteCost: isCloudResource(estateRecord)
            ? estateRecord.savingsAmount?.amount
            : "0",
        },
        currency,
      }),
    [
      currency,
      data.estateRecord?.co2e?.currentMonth,
      data.estateRecord?.cost,
      estateRecord,
    ]
  );

  const onApplicationUpdate = () => {
    void refetch();
    void refetchRules();
  };

  const provider = isCloudResource(estateRecord)
    ? estateRecord.provider
    : Provider.Aws;

  const applications = {
    loading: isLoadingRules,
    showApplicationsList: !!showApplicationsList,
    onApplicationUpdate,
    id: estateRecord.id,
    provider,
    resource: {
      ...estateRecord,
      provider,
    },
  };

  const linkedResources = {
    nordcloudId,
    linkedResources: data.links ?? [],
    onChange: refetch,
    cloudResourceData,
  };

  return (
    <Switch>
      <Case
        condition={
          !isAdminLite && cloudResourceData?.providerType === "ec2/instance"
        }
      >
        <Ec2InstancePage
          nid={nordcloudId}
          estateRecord={estateRecord}
          metadata={data.metadata}
          metatags={data.metaTags}
          applications={applications}
          costs={costs}
          breadcrumbs={data.breadcrumbLabels}
          loading={loading}
          error={error}
          linkedResources={linkedResources}
          resourceLifecycleDates={resourceLifecycleDates}
          enrichments={data.enrichments}
        />
      </Case>
      <Case
        condition={
          !isAdminLite && cloudResourceData?.providerType === "ec2/vpc"
        }
      >
        <Ec2VpcPage
          header={estateRecord}
          data={data}
          applications={applications}
          costs={costs}
          loading={loading}
          error={error}
        />
      </Case>
      <Case
        condition={
          !isAdminLite && cloudResourceData?.providerType === "ec2/subnet"
        }
      >
        <Ec2SubnetPage
          header={estateRecord}
          data={data}
          applications={applications}
          costs={costs}
          loading={loading}
          error={error}
        />
      </Case>
      <Case
        condition={
          !isAdminLite &&
          cloudResourceData?.providerType === "ec2/security-group"
        }
      >
        <Ec2SecurityGroupPage
          header={estateRecord}
          data={data}
          applications={applications}
          costs={costs}
          loading={loading}
          error={error}
        />
      </Case>
      <Case condition={showReservationInstancePage}>
        <ReservationInstancePage
          estateRecord={estateRecord}
          costs={costs}
          loading={loading}
          error={error}
          nid={nordcloudId}
          application={applications}
          metadata={data.metadata}
          resourceLifecycleDates={resourceLifecycleDates}
        />
      </Case>
      <Default>
        <DefaultEstateDetailsPage
          data={data}
          currency={currency}
          cloudResourceData={cloudResourceData}
          costs={costs}
          applications={applications}
          nordcloudId={nordcloudId}
          refetch={refetch}
          loading={loading}
          error={error}
        />
      </Default>
    </Switch>
  );
}
